// runMergeAndRecord.ts — Phase 3 of the mergeFile pipeline (FER-10394).
//
// Owns: dispatching on `MergePlan.kind`, running the appropriate
// `threeWayMerge` (for non-skipped variants), writing merged content to
// disk, populating the accumulator (mode-aware via MergeAccumulator), and
// classifying the resulting `FileResult`.
//
// Behavioral equivalence claim: for every `MergePlan` + `ResolvedInputs`
// combination, the returned `FileResult`, the on-disk content at
// `oursPath`, and the post-call accumulator state are byte-identical to
// the pre-refactor `mergeFile`'s outputs at the corresponding return path.
//
// Two deliberately-preserved divergences in the new-file-both branch
// (FER-10399): clean merge stores `merged.content` (not `plan.theirs`),
// conflict path skips the accumulator entirely. Both flagged with
// inline comments referencing FER-10399.

import { mkdir, writeFile } from "node:fs/promises";
import { dirname } from "node:path";
import type { MergeAccumulator } from "../accumulator/MergeAccumulator.js";
import { alignAutoversionToOurs } from "../autoversion.js";
import { threeWayMerge } from "../ThreeWayMerge.js";
import type { FileResult, StoredPatch } from "../types.js";
import type { MergePlan, ResolvedInputs } from "./MergePlan.js";

export interface RunMergeAndRecordArgs {
    plan: MergePlan;
    inputs: ResolvedInputs;
    patch: StoredPatch;
    accumulator: MergeAccumulator;
}

export async function runMergeAndRecord(args: RunMergeAndRecordArgs): Promise<FileResult> {
    const { plan, inputs, patch, accumulator } = args;
    const { resolvedPath, metadata, oursPath, ours } = inputs;

    if (plan.kind === "skipped") {
        return { file: resolvedPath, status: "skipped", reason: plan.reason };
    }

    // New-file-only-user: file user-created, generator never produced it.
    // Order matters: accumulator BEFORE writeFile, so the next patch's
    // fast-path-fallback sees the entry. Pre-refactor matches.
    if (plan.kind === "new-file-only-user") {
        accumulator.recordMerge(resolvedPath, plan.theirs, patch.base_generation);
        await mkdir(dirname(oursPath), { recursive: true });
        await writeFile(oursPath, plan.theirs);
        return { file: resolvedPath, status: "merged", reason: "new-file" };
    }

    // New-file-both: file created by both generator and user. Run 3-way
    // merge against an empty BASE so the diff3 algorithm can interleave
    // the two contributions.
    if (plan.kind === "new-file-both") {
        // ours is non-null here by buildMergePlan's variant invariant
        // (this branch is only emitted when base==null && ours!=null).
        const merged = threeWayMerge("", ours!, plan.theirs);
        await mkdir(dirname(oursPath), { recursive: true });
        await writeFile(oursPath, merged.content);
        // INC-866: when this `new-file-both` was produced because the base tree
        // is unreachable (shallow clone window too small), flag the result so the
        // service can warn. Otherwise the customer's edit can be lost silently.
        const unreachableFlag = plan.baseTreeUnreachable ? { baseTreeUnreachable: true } : {};
        if (merged.hasConflicts) {
            // FER-10399 preserved: no accumulator population on this path.
            // Asymmetric vs the main-merge conflict path (which calls
            // recordConflict). Tracked for triage; do not touch in
            // this refactor.
            return {
                file: resolvedPath,
                status: "conflict",
                conflicts: merged.conflicts,
                conflictReason: "new-file-both",
                conflictMetadata: metadata,
                ...unreachableFlag,
            };
        }
        // FER-10399 preserved: stores `merged.content` (with potentially
        // interleaved generator bytes), not `plan.theirs`. The
        // MergeAccumulator docstring says "stores each patch's THEIRS";
        // this site stores the merged result instead. Tracked for triage.
        accumulator.recordMerge(resolvedPath, merged.content, patch.base_generation);
        return { file: resolvedPath, status: "merged", ...unreachableFlag };
    }

    // Main 3-way merge path: ghost / snapshot / reconstructed / accumulator-base.
    // ours is non-null here by buildMergePlan's variant invariant.
    //
    // Autoversion alignment: the SDK version line is pipeline-owned. When the
    // pristine generation base (`inputs.base`) still carries the placeholder
    // (`--version AUTO`), align the version lines of BOTH the merge base and
    // THEIRS to OURS's freshly-resolved value. That makes the version line
    // identical across all three sides — stable context that diff3 never treats
    // as a change — so the merge takes the current version with no conflict even
    // when a customer edit sits adjacent to it, and a stale version baked into a
    // legacy THEIRS self-heals. No-op for fixed `--version` (no placeholder) or a
    // null base (new-file).
    const mergeBase =
        inputs.base != null ? alignAutoversionToOurs(inputs.base, ours!, plan.mergeBase) : plan.mergeBase;
    const theirs =
        inputs.base != null ? alignAutoversionToOurs(inputs.base, ours!, plan.theirs) : plan.theirs;
    const merged = threeWayMerge(mergeBase, ours!, theirs);
    await mkdir(dirname(oursPath), { recursive: true });
    await writeFile(oursPath, merged.content);

    // Update accumulator after merge so subsequent patches on the same file
    // have a structurally-correct THEIRS to anchor against.
    //
    // Cross-patch isolation guard: in REPLAY mode, `recordConflict` is a
    // no-op — the next iteration's intra-loop strip will reset disk to OURS,
    // and a poisoned accumulator entry could cause the next patch to see
    // false conflicts on regions it never touched.
    //
    // RESOLVE mode: `recordConflict` populates. We store `plan.theirs` (the
    // clean THEIRS, NOT `merged.content` with markers), so there's no marker
    // poisoning. A follow-up patch authored against post-this-patch
    // structure NEEDS this entry to anchor against.
    //
    // Mode dispatch lives entirely inside MergeAccumulator.recordConflict.
    if (merged.hasConflicts) {
        accumulator.recordConflict(resolvedPath, theirs, patch.base_generation);
        return {
            file: resolvedPath,
            status: "conflict",
            conflicts: merged.conflicts,
            conflictReason: plan.conflictReasonHint ?? "same-line-edit",
            conflictMetadata: metadata,
        };
    }
    accumulator.recordMerge(resolvedPath, theirs, patch.base_generation);

    return {
        file: resolvedPath,
        status: "merged",
        ...(merged.droppedContextLines && merged.droppedContextLines.length > 0
            ? { droppedContextLines: merged.droppedContextLines }
            : {}),
    };
}
